/**
 * Route guard for Course lessons reached by a scanned booklet QR.
 *
 * ONE PRINTED CODE PER SECTION, WHOEVER SCANS IT. There is no `?view=leader`
 * edition of a physical QR — the booklets carry the same code — so the guard
 * resolves the audience from the READER instead of from the URL: a leader is put
 * into the Leader's Guide, everyone else into the Student's. A disciple who scans a
 * leader-only section is refused, but that refusal happens in the lesson page, not
 * here: only the server can tell a section this reader may not have from one
 * reworded away since the booklet went to print (see `anchorLeaderOnly`).
 *
 * WHAT COUNTS AS A SCAN. A route with a `#section` hash. Ordinary in-app navigation
 * to a lesson carries none, and must not have its view flipped underneath it — a
 * leader who chose the student toggle keeps it while they browse.
 *
 * `?view=leader` is still honoured, and still refused for a non-leader. It is the
 * older link shape: share links and any `deep_link_config` row cut before this
 * change may carry it, and a member holding one should get the same answer as
 * before rather than a silent downgrade.
 *
 * Leader access is resolved SERVER-SIDE via `courses/checkLeaderAccess` (derived
 * from the access summary's `isLeaderGuideUser`). It deliberately does NOT read the
 * cached `profile.isLeader`: nothing in the boot sequence fetches the profile before
 * a deep-linked route renders, so that flag is its default `false` (or stale) at
 * cold deep-link time. The server check is the only trustworthy source, and it fails
 * closed.
 *
 * Lesson links that are neither anchored nor `?view=leader` are untouched — the
 * guard returns whatever the wrapped Courses gate returns and never runs the leader
 * check.
 */
export const LEADER_ONLY_REDIRECT = '/courses/leader-only';

export function createLeaderResourceGuard({ store, coursesGuard }) {
  return async to => {
    // Same feature-flag + pastoral gate as every other Courses route.
    const coursesResult = await coursesGuard(to);
    if (coursesResult) return coursesResult;

    const explicitLeaderView = to?.query?.view === 'leader';
    const scannedSection = !!to?.hash;

    // Neither a legacy leader link nor a scan — behave exactly like the plain
    // Courses gate, and leave the reader's chosen view alone.
    if (!explicitLeaderView && !scannedSection) return undefined;

    const isLeader = await store.dispatch('courses/checkLeaderAccess');

    // A link that NAMES the leader view is still refused outright: it addresses the
    // Leader's Guide as a whole, not a section, so there is nothing to fall back to.
    if (explicitLeaderView && !isLeader) return LEADER_ONLY_REDIRECT;

    // Put the reader in the guide they are entitled to. Committed for a non-leader
    // too: a leader who scans a code, then loses leader access, must not have the
    // persisted 'leader' view survive into a guide they can no longer read.
    store.commit('courses/setView', isLeader ? 'leader' : 'student');
    return undefined;
  };
}
